import type { ViewerShell } from '../shell';
import { canOpenMpr } from './gate';

/**
 * Light entry for MPR / MIP. vtk.js and the MPR/MIP views are fetched on first use
 * (dynamic import), so they cost nothing when a study is opened for plain reading.
 */

type MprModule = typeof import('./mpr');
type MipModule = typeof import('./mip');

let mprModule: MprModule | null = null;
let mipModule: MipModule | null = null;

async function loadMpr(): Promise<MprModule> {
  mprModule ??= await import('./mpr');
  return mprModule;
}

async function loadMip(): Promise<MipModule> {
  mipModule ??= await import('./mip');
  return mipModule;
}

export { canOpenMpr };

export function isMprOpen(): boolean {
  return mprModule?.isMprOpen() ?? false;
}

export function isMipOpen(): boolean {
  return mipModule?.isMipOpen() ?? false;
}

/** " MPR (legacy)": opens the axial / coronal / sagittal 1x3 view, or closes it. */
export async function toggleMpr(shell: ViewerShell): Promise<void> {
  await (await loadMpr()).toggleMpr(shell);
}

export async function openMip(shell: ViewerShell): Promise<void> {
  await (await loadMip()).openMip(shell);
}

export function closeMip(shell: ViewerShell): void {
  mipModule?.closeMip(shell);
}

/** Feature-module entry: `window.__viewerMpr` (bound to this shell) for the e2e checks. */
export function install(shell: ViewerShell): void {
  window.__viewerMpr = {
    openMpr: async () => (await loadMpr()).openMpr(shell),
    closeMpr: () => mprModule?.closeMpr(shell),
    openMip: () => openMip(shell),
    closeMip: () => closeMip(shell),
    toggleMpr: () => toggleMpr(shell),
    canOpenMpr: () => canOpenMpr(shell),
    isMprOpen,
    isMipOpen,
  };
}
